// /client/src/components/ContactContent.js
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import './ContactContent.css';

const Contact = (props) => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        messageType: '',
        fullName: '',
        email: '',
        confirmEmail: '',
        phone: '',
        message: '',
        orderNumber: '',
        eventDate: '',
        kindersLocation: '',
        newsletterConsent: true
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [emailMismatch, setEmailMismatch] = useState(false);

    // Environment-aware API URL configuration
    const getApiUrl = () => {
        const reactAppEnv = process.env.REACT_APP_ENVIRONMENT;
        const nodeEnv = process.env.NODE_ENV;
        
        if (reactAppEnv === 'production' || nodeEnv === 'production') {
            return '/api/contact';
        } else {
            return 'http://localhost:5001/api/contact';
        }
    };

    const handleLocationChange = (event) => {
        const selectedLocation = event.target.value;
        if (selectedLocation) {
            navigate(`/locations/${selectedLocation}`);
        }
    };

    const handleInputChange = (event) => {
        const { name, value, type, checked } = event.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear email mismatch error when user starts typing
        if (name === 'email' || name === 'confirmEmail') {
            setEmailMismatch(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsSubmitting(true);
        setSubmitMessage('');
        setEmailMismatch(false);

        // Validate email confirmation
        if (formData.email !== formData.confirmEmail) {
            setEmailMismatch(true);
            setSubmitMessage('Email addresses do not match. Please check and try again.');
            setIsSubmitting(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        try {
            const apiUrl = getApiUrl();
            
            // Remove confirmEmail before sending to API
            const { confirmEmail, ...dataToSend } = formData;
            
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(dataToSend)
            });

            if (response.ok) {
                const responseData = await response.json();
                
                // Instead of showing notification, hide form and show success message
                setIsFormSubmitted(true);
                setFormData({
                    messageType: '',
                    fullName: '',
                    email: '',
                    confirmEmail: '',
                    phone: '',
                    message: '',
                    orderNumber: '',
                    eventDate: '',
                    kindersLocation: '',
                    newsletterConsent: false
                });
            } else {
                // Enhanced error handling
                let errorMessage = 'Sorry, there was an error sending your message.';
                
                try {
                    const errorData = await response.json();
                    if (errorData.details && Array.isArray(errorData.details)) {
                        errorMessage = `Error: ${errorData.details.join(', ')}`;
                    } else if (errorData.error) {
                        errorMessage = errorData.error;
                    }
                } catch (parseError) {
                    if (response.status === 400) {
                        errorMessage = 'Please check your form data and try again.';
                    } else if (response.status === 429) {
                        errorMessage = 'Too many requests. Please wait a moment before trying again.';
                    } else if (response.status === 500) {
                        errorMessage = 'Server error. Please try again later.';
                    }
                }

                setSubmitMessage(errorMessage);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } catch (error) {
            console.error('Network error:', error);
            
            let errorMessage = '';
            if (error.name === 'TypeError' && error.message.includes('fetch')) {
                errorMessage = 'Cannot connect to server. Please check your connection and try again.';
            } else {
                errorMessage = `Network error: ${error.message}`;
            }
            
            setSubmitMessage(errorMessage);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const showOrderNumber = formData.messageType === 'order-problem';
    const showEventDate = formData.messageType === 'catering-inquiry' || formData.messageType === 'community-event';
    const showKindersLocation = formData.messageType && formData.messageType !== 'question' && formData.messageType !== 'other';

    return (
        <>
        <div className={`mt-3 column is-flex is-flex-direction-column ${props.className}`}>
            
            <div className="m-0 p-0 columns is-desktop">
                
                {/* Left Column - Contact Information */}
                <div className="p-0 my-0 mx-6 column">
                    <h1 className="mx-0 mt-0 mb-2 p-0 h1 colorBlack">{props.title}</h1>
                    
                    <p>Around here, good food and good folks go hand in hand. At Kinder's, we take pride in every brisket we smoke and every customer we serve. Did you have an issue with your order? Hosting something in the neighborhood? Want to share a kind word? Don't be shy. <a href="#message" className="hide-this-desktop">Send a message</a></p>
                    
                    <h2 className="mt-3 h2">Contact a Restaurant Location</h2>
                    <p>Trying to reach one of our seven East Bay locations? Select a location below to jump to its contact page.</p>

                    <div className="mb-5 location-selector-container">
                        <select 
                            className="location-selector"
                            onChange={handleLocationChange}
                            defaultValue=""
                        >
                            <option value="" disabled>Select a location...</option>
                            <option value="brentwood">Brentwood</option>
                            <option value="concord">Concord</option>
                            <option value="hercules">Hercules</option>
                            <option value="martinez">Martinez</option>
                            <option value="pleasant-hill">Pleasant Hill</option>
                            <option value="walnut-creek-ygnacio">Walnut Creek</option>
                        </select>
                    </div>

                    <h2 className="mt-0 h2">Restaurant and Catering Corporate Office</h2>
                    <p>We're here Monday to Friday, 8 AM to 4:30 PM.</p>
                    <div className="pt-2">
                        <p><span className="is-bold small">Address</span><br /><a href="https://g.co/kgs/uKHZK3k" target="_blank" rel="noopener noreferrer">1150 Burnett Ave., Suite D<br />Concord, CA 94520</a></p>
                        <p className="pt-2"><span className="is-bold small">Phone</span><br /><a href="tel:+19253637367">(925) 363-7367</a></p>
                        <p className="pt-2"><span className="is-bold small">Email</span><br /><a href="mailto:corporate@kinderscorp.com">corporate@kinderscorp.com</a></p>
                    </div>

                    <h2 className="mt-3 h2">Spices and Sauces Corporate Office</h2>
                    <p>We're here Monday to Friday, 8 AM to 4:30 PM.</p>
                    <div className="pt-2 pb-6">
                        <p><span className="is-bold small">Address</span><br /><a href="https://maps.app.goo.gl/drCpA9iHjke1QbzP8" target="_blank" rel="noopener noreferrer">2121 N. California Blvd., Suite 410<br />Walnut Creek, CA 94596</a></p>
                        <p className="pt-2"><span className="is-bold small">Email</span><br /><a href="mailto:info@kinders.com">info@kinders.com</a></p>
                    </div>
                </div>

                {/* Right Column - Contact Form or Success Message */}
                <div className="py-0 px-0 my-0 mx-4 column">
                    <div className="m-0 py-4 px-4 white-block contact-form-container" id="message">
                        <h2 className="mt-4 mb-2 mx-0 p-0 h2">
                            {isFormSubmitted ? 'Message Sent' : 'Send a Message'}
                        </h2>

                        {/* Error Banner */}
                        {(submitMessage || emailMismatch) && !isFormSubmitted && (
                            <div className="mb-4 p-4 br backgroundColorError">
                                <p className="small colorWhite">
                                    {emailMismatch 
                                        ? 'Email addresses do not match. Please check and try again.' 
                                        : submitMessage}
                                </p>
                            </div>
                        )}

                        {/* Show success message or form */}
                        {isFormSubmitted ? (
                            <div className="my-4">
                                <p>Thanks for reaching out to Kinder's. We truly appreciate you taking the time to connect with us. We will get back to you just as quick as we can step away from the smoker and check our inbox.</p>
                                
                                <p>We typically respond by email within 24 hours. Call our corporate office at <a href="tel:+19253637367">(925) 363-7367</a> if you need immediate assistance.</p>
                                
                                <p>Thank you,<br />The Kinder's Team</p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="contact-form">
                                {/* Message Type */}
                                <div className="field py-2">
                                    <label className="mt-0 mb-1 p-0 label colorBlack">What's this message about? *</label>
                                    <div className="m-0 p-0 control">
                                        <div className="m-0 p-0 location-selector-container">
                                            <select 
                                                className="location-selector"
                                                name="messageType" 
                                                value={formData.messageType} 
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="">Select an option...</option>
                                                <option value="order-problem">A problem with my order</option>
                                                <option value="catering-inquiry">Catering inquiry</option>
                                                <option value="community-event">Share a community event</option>
                                                <option value="question">Ask a question</option>
                                                <option value="compliment">Leave a compliment</option>
                                                <option value="feedback">Give feedback or a suggestion</option>
                                                <option value="other">Something else</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Conditional Kinder's Location Field */}
                                {showKindersLocation && (
                                    <div className="field pb-3">
                                        <label className="mt-0 mb-1 p-0 label colorBlack">Kinder's Location *</label>
                                        <div className="control">
                                            <div className="m-0 p-0 location-selector-container">
                                                <select 
                                                    className="location-selector"
                                                    name="kindersLocation" 
                                                    value={formData.kindersLocation} 
                                                    onChange={handleInputChange}
                                                    required={showKindersLocation}
                                                >
                                                    <option value="">Select location...</option>
                                                    <option value="Brentwood">Brentwood</option>
                                                    <option value="Concord">Concord</option>
                                                    <option value="Hercules">Hercules</option>
                                                    <option value="Martinez">Martinez</option>
                                                    <option value="Pleasant Hill">Pleasant Hill</option>
                                                    <option value="Walnut Creek">Walnut Creek</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Conditional Order Number Field */}
                                {showOrderNumber && (
                                    <div className="field pb-3">
                                        <label className="mt-0 mb-1 p-0 label colorBlack">Order Number (optional)</label>
                                        <div className="control">
                                            <input 
                                                className="input"
                                                type="text"
                                                name="orderNumber"
                                                value={formData.orderNumber}
                                                onChange={handleInputChange}
                                                placeholder="Enter your order number"
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Conditional Event Date Field */}
                                {showEventDate && (
                                    <div className="field pb-3">
                                        <label className="m-0 p-0 label colorBlack">Event Date (optional)</label>
                                        <div className="control">
                                            <input 
                                                className="input"
                                                type="date"
                                                name="eventDate"
                                                value={formData.eventDate}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Full Name */}
                                <div className="field pb-3">
                                    <label className={`m-0 p-0 label ${submitMessage ? ' colorBlack' : 'colorBlack'}`}>Full name *</label>
                                    <div className="control">
                                        <input 
                                            className="input"
                                            type="text"
                                            name="fullName"
                                            value={formData.fullName}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="Enter your full name"
                                        />
                                    </div>
                                </div>

                                {/* Email */}
                                <div className="field pb-3">
                                    <label className={`m-0 p-0 label ${emailMismatch ? 'colorError' : 'colorBlack'}`}>Email address *</label>
                                    <div className="control">
                                        <input 
                                            className={`input ${emailMismatch ? 'colorError' : ''}`}
                                            style={emailMismatch ? { borderColor: '#ff0000', color: '#ff0000' } : {}}
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="Enter your email address"
                                        />
                                    </div>
                                    {emailMismatch && (
                                        <p className="ml-1 pt-1 small colorError">Email addresses do not match.</p>
                                    )}
                                </div>

                                {/* Confirm Email */}
                                <div className="field pb-3">
                                    <label className={`m-0 p-0 label ${emailMismatch ? 'colorError' : 'colorBlack'}`}>Confirm email address *</label>
                                    <div className="control">
                                        <input 
                                            className={`input ${emailMismatch ? 'colorError' : ''}`}
                                            style={emailMismatch ? { borderColor: '#ff0000', color: '#ff0000' } : {}}
                                            type="email"
                                            name="confirmEmail"
                                            value={formData.confirmEmail}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="Re-enter your email address"
                                        />
                                    </div>
                                    {emailMismatch && (
                                        <p className="ml-1 pt-1 small colorError">Email addresses do not match.</p>
                                    )}
                                </div>

                                {/* Phone */}
                                <div className="field pb-3">
                                    <label className={`m-0 p-0 label ${submitMessage ? 'colorBlack' : 'colorBlack'}`}>Phone number</label>
                                    <div className="control">
                                        <input 
                                            className="input"
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            placeholder="Enter your phone number (optional)"
                                        />
                                    </div>
                                </div>

                                {/* Message */}
                                <div className="field pb-3">
                                    <label className={`m-0 p-0 label ${submitMessage ? 'colorBlack' : 'colorBlack'}`}>How can we help? *</label>
                                    <div className="control">
                                        <textarea 
                                            className="textarea"
                                            name="message"
                                            value={formData.message}
                                            onChange={handleInputChange}
                                            required
                                            rows="4"
                                            placeholder="Tell us how we can help you..."
                                        ></textarea>
                                    </div>
                                </div>

                                {/* Newsletter Consent Checkbox */}
                                <div className="field pb-3">
                                    <div className="control">
                                        <label className="checkbox">
                                            <input 
                                                type="checkbox" 
                                                name="newsletterConsent"
                                                checked={formData.newsletterConsent}
                                                onChange={handleInputChange}
                                            />
                                            <span className="small">
                                                I'd like to receive Kinder's newsletter with promotions and BBQ tips (optional)
                                            </span>
                                        </label>
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="field">
                                    <div className="control">
                                        <button 
                                            className="button"
                                            type="submit"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? 'Sending...' : 'Send Message'}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
        </>
    );
}

export default Contact;